import get from 'lodash-es/get'
import api from '#/configs/api'
import { NEIGHBORHOOD_RESIDENTIAL_SUBDIVISION_LEVEL } from '#/consts/neighborhood_level'
import invokeApi from '#/utils/invokeApi'
import { getNeighborhood } from '#/utils/properties'

export function getDetail(
  params: TopHap.Service.GetDetailRequest,
  abortController?: AbortController
) {
  return invokeApi<TopHap.Service.ListResponse<TopHap.PropertyTransaction>>({
    baseUrl: api.baseUrl,
    endpoint: api.properties.getDetail,
    method: 'POST',
    body: params,
    withUserId: true,
    abortController
  })
}

export function getMedia(
  attomId: string,
  abortController?: AbortController
): Promise<TopHap.PropertyMedia | undefined> {
  return getDetail({
    attomId,
    factsOnly: true,
    photosOnly: true,
    sort: 'desc',
    withTypes: true,
    types: ['Listing']
  }, abortController).then(({ items }) => get(items, ['0', 'media']))
}

export function getSchoolDetails(
  payload: TopHap.Service.GetSchoolDetailsRequest,
  abortController?: AbortController
) {
  return invokeApi<TopHap.Service.ListResponse<TopHap.School>>({
    baseUrl: api.baseUrl,
    endpoint: api.properties.getSchoolDetails,
    method: 'POST',
    body: payload,
    withUserId: true,
    abortController
  })
}

export function getComparableParams(property: TopHap.Property, light = true) {
  const neighborhood = getNeighborhood(
    property.zones,
    NEIGHBORHOOD_RESIDENTIAL_SUBDIVISION_LEVEL
  )

  const getMinMax = (value: number, small = false) => ({
    origin: value,
    min: small ? value - 1 : value * 0.8,
    max: small ? value + 1 : value * 1.2
  })

  const getAreaFilter = (value: number) => {
    if (value > 0) {
      return {
        min: value * 0.75,
        max: value * 1.25
      }
    }
    return undefined
  }

  const getPropertyUseFilter = (
    use: TopHap.PropertyUse,
    useGroup: TopHap.PropertyUseGroup
  ): TopHap.Service.ComparablesRequestPropertyType[] => {
    let values: TopHap.Service.ComparablesRequestPropertyType[] = []
    if (useGroup === 'Residential') {
      if (use === 'Townhouse' || use === 'Condo') {
        values = ['Townhouse', 'Condo']
      } else if (use !== 'House' && use !== 'Multi-family') {
        values = ['Other']
      } else {
        values = [use]
      }
    } else {
      values = [useGroup]
    }
    return values
  }

  const params = {
    size: 6,
    light,
    onmarket: false,
    attomId: property.id,
    filters: {
      property_type: {
        values: getPropertyUseFilter(
          property.PropertyUse,
          property.PropertyUseGroup
        )
      },
      living_area: getAreaFilter(property.LivingSqft)
    },
    zone: { id: neighborhood?.id },
    time: {
      months: 3,
      weight: 2
    },
    location: {
      lon: property.location[0],
      lat: property.location[1]
    },
    beds: {
      ...getMinMax(property.BedsCount, true),
      scale: 1,
      decay: 0.5
    },
    baths: {
      ...getMinMax(property.BathsDecimal, true),
      scale: 1,
      decay: 0.5
    },
    area: {
      ...getMinMax(property.LivingSqft),
      scale: 500,
      decay: 0.5
    },
    lot: {
      ...getMinMax(property.LotAcres),
      scale: 0.4,
      decay: 0.5
    },
    year: {
      ...getMinMax(property.YearBuilt),
      scale: 10,
      decay: 0.5
    }
  } as TopHap.Service.GetComparablesRequest

  ['beds', 'baths', 'area', 'lot', 'year'].forEach(e => {
    const metric = e as keyof TopHap.Service.GetComparablesRequest
    if (
      typeof params[metric] === 'object' &&
        ((params[metric] as any).origin == null ||
          Number.isNaN((params[metric] as any).origin))
    ) {
      delete params[metric]
    }
  })

  return params
}

export function getComparables(
  payload: TopHap.Service.GetComparablesRequest,
  abortController?: AbortController
) {
  return invokeApi<TopHap.Service.ListResponse<TopHap.PropertyWithAnalyticsMetrics>>({
    baseUrl: api.baseUrl,
    endpoint: api.properties.getComparables,
    method: 'POST',
    body: payload,
    withUserId: true,
    abortController
  })
}

export function getNearbyStatistics(
  payload: TopHap.Service.GetNearbyStatisticsRequest,
  abortController?: AbortController
) {
  return invokeApi<TopHap.Service.GetNearbyStatisticsResponse>({
    baseUrl: api.baseUrl,
    endpoint: api.properties.getNearbyStatistics,
    method: 'POST',
    body: payload,
    withUserId: true,
    abortController
  })
}

export function submitLead(
  payload: TopHap.Service.SubmitLeadRequest,
  abortController?: AbortController
) {
  return invokeApi({
    baseUrl: api.baseV5Url,
    endpoint: api.properties.submitLead,
    method: 'POST',
    body: payload,
    withUserId: true,
    abortController,
    auth: true
  })
}
